import React from "react";
import { Badge } from "src/components/ui/Badge";
import { Button } from "src/components/ui/Button";
import {
  Clock,
  Calendar as CalendarIcon,
  Video,
  Send,
  Star,
  BookOpen,
  User,
} from "lucide-react";
import { Link } from "react-router-dom";
import AuthenticatedImage from "src/components/ui/AuthenticatedImage";
import { getSessionStatus } from "src/utils/sessionUtils";

// Helper function to format session time
const formatSessionTime = (scheduledAt: string) => {
  const date = new Date(scheduledAt);
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };
};

// Helper function to get student name
const getStudentName = (studentProfile: any) => {
  if (!studentProfile) return 'Unknown Student';
  return `${studentProfile.first_name || ''} ${studentProfile.last_name || ''}`.trim() || 'Unknown Student';
};

// Helper function to get student display name
const getStudentDisplayName = (studentProfile: any) => {
  return getStudentName(studentProfile);
};

// Helper function to get topic name
const getTopicName = (topic: any) => {
  return topic?.name || 'Unknown Topic';
};

// Helper function to get subtopic name
const getSubtopicName = (subtopic: any) => {
  return subtopic?.name || null;
};

// Helper function to get student photo - returns null if no photo so we can show initials
const getStudentPhoto = (studentProfile: any) => {
  return studentProfile?.profile_picture_url || null;
};

// Helper function to get student initials for fallback
const getStudentInitials = (studentProfile: any) => {
  if (!studentProfile) return 'US';
  const firstName = studentProfile.first_name || '';
  const lastName = studentProfile.last_name || '';
  const firstInitial = firstName.charAt(0).toUpperCase() || 'U';
  const lastInitial = lastName.charAt(0).toUpperCase() || 'S';
  return `${firstInitial}${lastInitial}`;
};

// Helper function to generate human-readable session ID
const generateSessionId = (sessionUuid: string) => {
  // Create a simple hash from UUID to generate consistent session numbers
  let hash = 0;
  for (let i = 0; i < sessionUuid.length; i++) {
    const char = sessionUuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Ensure positive number and add base offset
  const sessionNumber = Math.abs(hash % 9000) + 1000;
  return `S-${sessionNumber}`;
};

// Helper function to get subject name from topic
const getSubjectName = (topic: any) => {
  // Use the subject data from the topic if available, otherwise fallback to topic name
  return topic?.subject?.name || topic?.name || 'Unknown Subject';
};


interface TutorSessionCardProps {
  session: any;
  showActions?: boolean;
  showSessionId?: boolean;
  onJoin?: (session: any) => void;
  onSendReminder?: (session: any) => void;
}

const TutorSessionCard: React.FC<TutorSessionCardProps> = ({
  session,
  showActions = true,
  showSessionId = false,
  onJoin,
  onSendReminder
}) => {
  const { date, time } = formatSessionTime(session.scheduled_at);
  const studentDisplayName = getStudentDisplayName(session.student_profile);
  const studentPhoto = getStudentPhoto(session.student_profile);
  const studentInitials = getStudentInitials(session.student_profile);
  const topicName = getTopicName(session.topic);
  const subtopicName = getSubtopicName(session.subtopic);
  const sessionId = generateSessionId(session.id);
  const sessionStatus = getSessionStatus(session);

  const handleJoin = () => {
    if (onJoin) {
      onJoin(session);
    }
  };

  const handleSendReminder = () => {
    if (onSendReminder) {
      onSendReminder(session);
    }
  };

  // Debug: Log the student profile data
  console.log('Student profile data:', session.student_profile);
  console.log('Student photo URL:', studentPhoto);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
      {showSessionId && (
        <div className="px-6 pt-4">
          <Badge variant="outline" className="text-xs">
            {sessionId}
          </Badge>
        </div>
      )}
      
      <div className="p-6">
        {/* Header Section - Student Info */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {studentPhoto ? (
              <AuthenticatedImage
                src={studentPhoto}
                alt={studentDisplayName}
                className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
                fallback={
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm ring-2 ring-gray-100">
                    {studentInitials}
                  </div>
                }
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm ring-2 ring-gray-100">
                {studentInitials}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <h3 className="font-semibold text-gray-900">{studentDisplayName}</h3>
              </div>
            </div>
          </div>
          
          <Badge
            variant={sessionStatus.variant}
            className={`capitalize ${sessionStatus.status === 'live' ? 'animate-pulse bg-red-100 text-red-800 border-red-200' : ''}`}
          >
            {sessionStatus.label}
          </Badge>
        </div>

        {/* Session Content Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Session Topic/Subtopic */}
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-blue-600" />
                <div>
                  {subtopicName ? (
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{subtopicName}</h4>
                      <p className="text-sm text-gray-600">{topicName}</p>
                    </div>
                  ) : (
                    <h4 className="font-semibold text-gray-900 text-lg">{topicName}</h4>
                  )}
                </div>
              </div>
              
              {/* Batch Information */}
              {session.batch?.name && (
                <div className="mt-2">
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                    {session.batch.name}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Session Details */}
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-4 w-4" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{time}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">{session.duration_min} min</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="text-xs capitalize">
                {session.session_type}
              </Badge>
            </div>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex justify-end space-x-2">
            {onSendReminder && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendReminder}
                className="text-gray-600 hover:text-gray-800"
              >
                <Send className="h-4 w-4 mr-1" />
                Send Reminder
              </Button>
            )}
            {onJoin && sessionStatus.canJoin && (
              <Button
                size="sm"
                onClick={handleJoin}
                className={`${sessionStatus.status === 'live' ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                <Video className="h-4 w-4 mr-1" />
                {sessionStatus.status === 'live' ? 'Join Now' : 'Join'}
              </Button>
            )}
            {/* Fallback to Link-based actions if no handlers provided */}
            {!onJoin && !onSendReminder && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-800"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Send Reminder
                </Button>
                <Button
                  size="sm"
                  asChild
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Link to={`/join-session/${session.id}`}>
                    <Video className="h-4 w-4 mr-1" />
                    Join
                  </Link>
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorSessionCard;
export { 
  formatSessionTime, 
  getStudentDisplayName, 
  getTopicName, 
  getSubtopicName, 
  getStudentPhoto, 
  generateSessionId, 
  getSubjectName,
  getStudentName,
  getStudentInitials
};